/**
 * LATE NIGHT LIPS — Theme JavaScript
 * Storefront interactions plus Shopify Theme Editor section re-initialization.
 */

(function () {
  'use strict';

  function initHeader(root) {
    const header = root.querySelector ? root.querySelector('#site-header') : null;
    if (header && !header.dataset.lnlInitialized) {
      header.dataset.lnlInitialized = 'true';
      const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 60);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    const menuToggle = root.querySelector ? root.querySelector('[data-menu-toggle]') : null;
    const mobileMenu = root.querySelector ? root.querySelector('#mobile-menu') : null;
    const menuClose = root.querySelector ? root.querySelector('[data-menu-close]') : null;

    if (menuToggle && mobileMenu && !menuToggle.dataset.lnlInitialized) {
      menuToggle.dataset.lnlInitialized = 'true';
      let previousOverflow = '';
      const closeMenu = () => {
        mobileMenu.classList.remove('is-open');
        mobileMenu.inert = true;
        mobileMenu.setAttribute('aria-hidden', 'true');
        menuToggle.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = previousOverflow;
        menuToggle.focus({ preventScroll: true });
      };
      menuToggle.addEventListener('click', () => {
        previousOverflow = document.body.style.overflow;
        mobileMenu.inert = false;
        mobileMenu.classList.add('is-open');
        mobileMenu.setAttribute('aria-hidden', 'false');
        menuToggle.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        menuClose?.focus({ preventScroll: true });
      });
      menuClose?.addEventListener('click', closeMenu);
      mobileMenu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeMenu();
        } else if (event.key === 'Tab') {
          const controls = [...mobileMenu.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]')];
          const first = controls[0], last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault(); last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault(); first?.focus();
          }
        }
      });
      window.matchMedia('(min-width: 769px)').addEventListener('change', (event) => {
        if (event.matches && mobileMenu.classList.contains('is-open')) closeMenu();
      });
    }
  }

  function initReveals(root) {
    const revealEls = root.querySelectorAll ? root.querySelectorAll('.reveal:not([data-lnl-initialized])') : [];
    if (!revealEls.length) return;

    revealEls.forEach((element) => {
      element.dataset.lnlInitialized = 'true';
    });

    if (!('IntersectionObserver' in window) || (window.Shopify && window.Shopify.designMode)) {
      revealEls.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -60px 0px' });

    revealEls.forEach((element) => observer.observe(element));
  }

  function initHeroVideos(root) {
    const videos = root.querySelectorAll ? root.querySelectorAll('.hero__video:not([data-lnl-initialized])') : [];
    videos.forEach((video) => {
      video.dataset.lnlInitialized = 'true';
      video.controls = false;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      const showPlayingVideo = () => video.classList.add('is-playing');
      const showPoster = () => video.classList.remove('is-playing');
      video.addEventListener('playing', showPlayingVideo);
      video.addEventListener('pause', showPoster);
      video.addEventListener('error', showPoster);

      const attemptPlayback = () => {
        if (document.visibilityState === 'hidden') return;

        video.muted = true;
        video.defaultMuted = true;
        const playback = video.play();
        if (playback && typeof playback.then === 'function') {
          playback.then(showPlayingVideo).catch(showPoster);
        } else if (!video.paused) {
          showPlayingVideo();
        }
      };

      const retryPlayback = () => {
        if (video.paused && !video.ended) attemptPlayback();
      };

      video.addEventListener('loadeddata', retryPlayback, { once: true });
      video.addEventListener('canplay', retryPlayback, { once: true });
      window.addEventListener('pageshow', retryPlayback);
      document.addEventListener('visibilitychange', retryPlayback);
      document.addEventListener('pointerdown', retryPlayback, { once: true, passive: true });
      document.addEventListener('touchstart', retryPlayback, { once: true, passive: true });
      document.addEventListener('keydown', retryPlayback, { once: true });

      attemptPlayback();
      window.setTimeout(retryPlayback, 400);
      window.setTimeout(retryPlayback, 1600);
    });
  }

  function initGallery(root) {
    const gallery = root.querySelector ? root.querySelector('.product-page__gallery') : null;
    if (!gallery || gallery.dataset.lnlInitialized) return;

    const mainFrame = gallery.querySelector('#product-gallery-main');
    const mainImg = gallery.querySelector('#gallery-active-img');
    const counter = gallery.querySelector('#gallery-counter');
    const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    const expandButton = gallery.querySelector('[data-gallery-expand]');
    const lightbox = gallery.querySelector('[data-gallery-lightbox]');
    const lightboxImg = gallery.querySelector('[data-gallery-lightbox-image]');
    const lightboxCounter = gallery.querySelector('[data-gallery-lightbox-counter]');
    const lightboxStage = gallery.querySelector('[data-gallery-lightbox-stage]');
    const lightboxClose = gallery.querySelector('[data-gallery-lightbox-close]');
    const lightboxPrev = gallery.querySelector('[data-gallery-lightbox-prev]');
    const lightboxNext = gallery.querySelector('[data-gallery-lightbox-next]');
    if (!mainFrame || !mainImg || !thumbs.length) return;

    gallery.dataset.lnlInitialized = 'true';
    const thumbList = Array.from(thumbs);
    const mainSizes = mainImg.getAttribute('sizes') || '';
    const isMobile = () => window.matchMedia('(max-width: 768px)').matches;
    let activeIndex = Math.max(0, thumbList.findIndex((thumb) => thumb.classList.contains('product-page__thumb--active')));
    let activeRequest = 0;
    let suppressClickUntil = 0;
    let suppressLightboxClickUntil = 0;
    let restoreFocus = null;
    let bodyScrollState = null;

    const getThumbData = (thumb) => ({
      src: thumb.getAttribute('data-src') || '',
      srcset: thumb.getAttribute('data-srcset') || '',
      alt: thumb.getAttribute('data-alt') || '',
      index: Number.parseInt(thumb.getAttribute('data-index'), 10) || 0
    });

    const formatCounter = (index) => `${String(index + 1).padStart(2, '0')} / ${String(thumbList.length).padStart(2, '0')}`;

    const setImageSource = (image, data, sizes) => {
      if (!image) return;
      if (data.srcset) {
        image.setAttribute('srcset', data.srcset);
      } else {
        image.removeAttribute('srcset');
      }
      if (sizes) image.setAttribute('sizes', sizes);
      image.src = data.src;
      image.alt = data.alt;
    };

    const syncLightboxImage = (data) => {
      if (!lightboxImg) return;
      setImageSource(lightboxImg, data, '100vw');
      if (lightboxCounter) lightboxCounter.textContent = formatCounter(data.index);
    };

    const preloadImage = (thumb, lightboxSize) => {
      if (!thumb) return;
      const marker = lightboxSize ? 'lnlLightboxPreloaded' : 'lnlPreloaded';
      if (thumb.dataset[marker]) return;
      thumb.dataset[marker] = 'true';
      const data = getThumbData(thumb);
      const img = new Image();
      img.sizes = lightboxSize ? '100vw' : mainSizes;
      if (data.srcset) img.srcset = data.srcset;
      img.src = data.src;
    };

    const warmNeighbours = () => {
      preloadImage(thumbList[(activeIndex + 1) % thumbList.length], lightbox && lightbox.open);
      preloadImage(thumbList[(activeIndex - 1 + thumbList.length) % thumbList.length], lightbox && lightbox.open);
    };

    const updateActiveControls = (nextIndex) => {
      activeIndex = nextIndex;
      if (counter) counter.textContent = formatCounter(activeIndex);
      if (lightboxCounter) lightboxCounter.textContent = formatCounter(activeIndex);
      thumbList.forEach((item, index) => {
        const isActive = index === activeIndex;
        item.classList.toggle('product-page__thumb--active', isActive);
        item.style.borderColor = isActive ? '' : 'transparent';
        if (isActive) {
          item.setAttribute('aria-current', 'true');
          if (item.scrollIntoView && !(lightbox && lightbox.open)) {
            const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            item.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
          }
        } else {
          item.removeAttribute('aria-current');
        }
      });
    };

    const selectImage = (requestedIndex) => {
      const nextIndex = (requestedIndex + thumbList.length) % thumbList.length;
      if (nextIndex === activeIndex) return;
      const thumb = thumbList[nextIndex];
      const data = getThumbData(thumb);
      const requestId = ++activeRequest;
      updateActiveControls(nextIndex);
      preloadImage(thumb, lightbox && lightbox.open);

      const applyImage = () => {
        if (requestId !== activeRequest) return;
        mainImg.style.opacity = '0';
        if (lightbox && lightbox.open && lightboxImg) lightboxImg.style.opacity = '0';
        window.setTimeout(() => {
          if (requestId !== activeRequest) return;
          setImageSource(mainImg, data, mainSizes);
          mainImg.style.opacity = '1';
          if (lightbox && lightbox.open && lightboxImg) {
            syncLightboxImage(data);
            lightboxImg.style.opacity = '1';
          }
        }, 90);
      };

      const loader = new Image();
      loader.sizes = lightbox && lightbox.open ? '100vw' : mainSizes;
      if (data.srcset) loader.srcset = data.srcset;
      loader.src = data.src;
      const decodeThenApply = () => {
        if (typeof loader.decode === 'function') {
          loader.decode().then(applyImage).catch(applyImage);
        } else {
          applyImage();
        }
      };
      if (loader.complete && loader.naturalWidth > 0) {
        decodeThenApply();
      } else {
        loader.addEventListener('load', decodeThenApply, { once: true });
        loader.addEventListener('error', applyImage, { once: true });
      }
    };

    const goTo = (index) => {
      selectImage(index);
      warmNeighbours();
    };

    const lockPageScroll = () => {
      if (bodyScrollState) return;
      bodyScrollState = {
        y: window.scrollY,
        position: document.body.style.position,
        top: document.body.style.top,
        left: document.body.style.left,
        right: document.body.style.right,
        width: document.body.style.width
      };
      document.body.style.position = 'fixed';
      document.body.style.top = `-${bodyScrollState.y}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.documentElement.classList.add('gallery-lightbox-open');
    };

    const unlockPageScroll = () => {
      document.documentElement.classList.remove('gallery-lightbox-open');
      if (!bodyScrollState) return;
      const { y, position, top, left, right, width } = bodyScrollState;
      document.body.style.position = position;
      document.body.style.top = top;
      document.body.style.left = left;
      document.body.style.right = right;
      document.body.style.width = width;
      bodyScrollState = null;
      window.scrollTo(0, y);
    };

    const openLightbox = () => {
      if (!isMobile() || !lightbox || !lightboxImg) return;
      const data = getThumbData(thumbList[activeIndex]);
      syncLightboxImage(data);
      lightboxImg.style.opacity = '1';
      preloadImage(thumbList[activeIndex], true);
      warmNeighbours();
      restoreFocus = document.activeElement === document.body ? (expandButton || mainFrame) : document.activeElement;
      lockPageScroll();
      if (typeof lightbox.showModal === 'function') {
        lightbox.showModal();
      } else {
        lightbox.setAttribute('open', '');
      }
      window.requestAnimationFrame(() => {
        if (lightboxClose) lightboxClose.focus({ preventScroll: true });
      });
    };

    const closeLightbox = () => {
      if (!lightbox || !lightbox.hasAttribute('open')) return;
      if (lightboxImg) lightboxImg.style.opacity = '1';
      if (typeof lightbox.close === 'function') {
        lightbox.close();
      } else {
        lightbox.removeAttribute('open');
        unlockPageScroll();
        if (restoreFocus && restoreFocus.focus) restoreFocus.focus({ preventScroll: true });
      }
    };

    let delay = 0;
    const warmGallery = () => {
      thumbList.forEach((thumb) => {
        window.setTimeout(() => preloadImage(thumb, false), delay);
        delay += 150;
      });
    };
    if (document.readyState === 'complete') {
      window.setTimeout(warmGallery, 300);
    } else {
      window.addEventListener('load', () => window.setTimeout(warmGallery, 300), { once: true });
    }

    thumbList.forEach((thumb) => {
      thumb.addEventListener('pointerenter', () => preloadImage(thumb, false), { passive: true });
      thumb.addEventListener('touchstart', () => preloadImage(thumb, false), { passive: true });
      thumb.addEventListener('click', () => selectImage(getThumbData(thumb).index));
    });

    let swipeStartX = 0;
    let swipeStartY = 0;
    let swipeTracking = false;

    mainFrame.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      swipeTracking = true;
      swipeStartX = event.touches[0].clientX;
      swipeStartY = event.touches[0].clientY;
      warmNeighbours();
    }, { passive: true });

    mainFrame.addEventListener('touchend', (event) => {
      if (!swipeTracking) return;
      swipeTracking = false;
      const touch = event.changedTouches && event.changedTouches[0];
      if (!touch) return;
      const deltaX = touch.clientX - swipeStartX;
      const deltaY = touch.clientY - swipeStartY;
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) return;
      suppressClickUntil = Date.now() + 650;
      goTo(deltaX < 0 ? activeIndex + 1 : activeIndex - 1);
    }, { passive: true });

    mainFrame.addEventListener('touchcancel', () => { swipeTracking = false; }, { passive: true });
    mainFrame.style.cursor = 'pointer';
    mainFrame.addEventListener('click', (event) => {
      if (event.target.closest('[data-gallery-expand]')) return;
      if (Date.now() < suppressClickUntil) return;
      const rect = mainFrame.getBoundingClientRect();
      const ratio = (event.clientX - rect.left) / rect.width;
      warmNeighbours();
      if (isMobile()) {
        if (ratio < 0.34) {
          goTo(activeIndex - 1);
        } else if (ratio > 0.66) {
          goTo(activeIndex + 1);
        } else {
          openLightbox();
        }
        return;
      }
      goTo(ratio < 0.25 ? activeIndex - 1 : activeIndex + 1);
    });

    mainFrame.setAttribute('tabindex', '0');
    mainFrame.setAttribute('role', 'group');
    mainFrame.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(activeIndex + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(activeIndex - 1);
      } else if ((event.key === 'Enter' || event.key === ' ') && isMobile()) {
        event.preventDefault();
        openLightbox();
      }
    });

    if (expandButton) {
      expandButton.addEventListener('click', (event) => {
        event.stopPropagation();
        if (Date.now() < suppressClickUntil) return;
        openLightbox();
      });
    }

    if (lightbox && lightboxStage) {
      let lightboxStartX = 0;
      let lightboxStartY = 0;
      let lightboxTracking = false;

      lightboxStage.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) return;
        lightboxTracking = true;
        lightboxStartX = event.touches[0].clientX;
        lightboxStartY = event.touches[0].clientY;
        warmNeighbours();
      }, { passive: true });

      lightboxStage.addEventListener('touchend', (event) => {
        if (!lightboxTracking) return;
        lightboxTracking = false;
        const touch = event.changedTouches && event.changedTouches[0];
        if (!touch) return;
        const deltaX = touch.clientX - lightboxStartX;
        const deltaY = touch.clientY - lightboxStartY;
        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) return;
        suppressLightboxClickUntil = Date.now() + 650;
        goTo(deltaX < 0 ? activeIndex + 1 : activeIndex - 1);
      }, { passive: true });

      lightboxStage.addEventListener('touchcancel', () => { lightboxTracking = false; }, { passive: true });
      lightboxStage.addEventListener('click', (event) => {
        if (event.target.closest('button') || Date.now() < suppressLightboxClickUntil) return;
        const rect = lightboxStage.getBoundingClientRect();
        const ratio = (event.clientX - rect.left) / rect.width;
        goTo(ratio < 0.5 ? activeIndex - 1 : activeIndex + 1);
      });

      lightbox.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          goTo(activeIndex + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goTo(activeIndex - 1);
        }
      });

      lightbox.addEventListener('close', () => {
        if (lightboxImg) lightboxImg.style.opacity = '1';
        unlockPageScroll();
        if (restoreFocus && restoreFocus.focus) restoreFocus.focus({ preventScroll: true });
      });
    }

    if (lightboxClose) lightboxClose.addEventListener('click', closeLightbox);
    if (lightboxPrev) lightboxPrev.addEventListener('click', () => goTo(activeIndex - 1));
    if (lightboxNext) lightboxNext.addEventListener('click', () => goTo(activeIndex + 1));
    if (thumbList[activeIndex]) thumbList[activeIndex].setAttribute('aria-current', 'true');
    if (thumbList.length < 2) {
      [lightboxPrev, lightboxNext].forEach((button) => {
        if (!button) return;
        button.hidden = true;
        button.disabled = true;
      });
    }
  }

  function initIngredientJump(root) {
    if (!root.querySelector) return;
    const trigger = root.querySelector('[data-open-inci]');
    const drawer = root.querySelector('[data-inci-drawer]');
    if (!trigger || !drawer || trigger.dataset.lnlInitialized) return;

    trigger.dataset.lnlInitialized = 'true';
    trigger.addEventListener('click', () => {
      drawer.open = true;
      window.requestAnimationFrame(() => {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        drawer.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        const summary = drawer.querySelector('summary');
        if (summary) summary.focus({ preventScroll: true });
      });
    });
  }

  function lnlTrack(name, surface) {
    try {
      if (typeof window.clarity === 'function') {
        window.clarity('set', 'lnl_duo_surface', surface);
        window.clarity('event', name);
      }
    } catch (_) { /* analytics must never block the cart */ }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, { surface });
      }
    } catch (_) { /* analytics must never block the cart */ }
  }

  function initCart(root) {
    if (!root.querySelectorAll) return;

    const form = root.querySelector('.cart-page__form');
    if (!form) return;

    const buttons = root.querySelectorAll('[data-qty-minus], [data-qty-plus]');
    const inputs = root.querySelectorAll('.cart-item__qty-input');
    const removeLinks = root.querySelectorAll('[data-cart-remove]');
    const errorMessage = root.querySelector('[data-cart-error]');
    const duoUpsell = root.querySelector('[data-duo-upsell]');
    const duoUpgrade = root.querySelector('[data-duo-upgrade]');
    const duoStatus = root.querySelector('[data-duo-status]');
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD';
    const locale = document.documentElement.lang || 'en-CA';
    const cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    function formatMoney(cents) {
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol'
        }).format(cents / 100);
      } catch (_) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    function setBusy(isBusy) {
      form.setAttribute('aria-busy', String(isBusy));
      form.querySelectorAll('button').forEach((button) => {
        button.disabled = isBusy;
      });
      if (form.closest('[data-cart-drawer]')) inputs.forEach((input) => { input.disabled = isBusy; });
      removeLinks.forEach((link) => {
        link.setAttribute('aria-disabled', String(isBusy));
        link.style.pointerEvents = isBusy ? 'none' : '';
      });
    }

    function showError(message) {
      if (!errorMessage) return;
      errorMessage.textContent = message;
      errorMessage.hidden = !message;
    }

    function buildCartUrl(hash) {
      const cartUrl = new URL(`${cartRoot}cart`, window.location.origin);
      const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewThemeId) cartUrl.searchParams.set('preview_theme_id', previewThemeId);
      if (hash) cartUrl.hash = hash;
      return cartUrl.toString();
    }

    function replaceCartSection(cart, options = {}) {
      const sectionHtml = cart.sections && cart.sections['main-cart'];
      const currentSection = form.closest('.section-cart');
      if (!sectionHtml || !currentSection) return false;

      const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const nextSection = parsed.querySelector('.section-cart');
      if (!nextSection) return false;

      const savedScrollY = window.scrollY;
      const savedDrawerScroll = form.querySelector('.cart-page__items')?.scrollTop || 0;
      const cartCount = document.querySelector('#cart-count');
      if (cartCount) cartCount.textContent = cart.item_count;

      currentSection.innerHTML = nextSection.innerHTML;
      currentSection.className = nextSection.className;
      initCart(currentSection);
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart } }));
      if (currentSection.closest('[data-cart-drawer]')) {
        window.LNLCartDrawer?.refresh();
        const heading = currentSection.querySelector('.cart-page__title');
        const focusTarget = [...currentSection.querySelectorAll('.cart-item__qty-input')]
          .find((input) => input.dataset.key === options.focusKey);
        const drawerItems = currentSection.querySelector('.cart-page__items');
        if (drawerItems) drawerItems.scrollTop = options.scrollToTop ? 0 : savedDrawerScroll;
        if (options.scrollToTop) {
          heading?.focus({ preventScroll: true });
        } else {
          (focusTarget || heading)?.focus({ preventScroll: true });
        }
        return true;
      }

      window.requestAnimationFrame(() => {
        if (options.scrollToTop) {
          const heading = currentSection.querySelector('.cart-page__title');
          if (heading) heading.setAttribute('tabindex', '-1');
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
          window.setTimeout(() => {
            if (heading) heading.focus({ preventScroll: true });
          }, reduceMotion ? 0 : 320);
          return;
        }

        window.scrollTo({ top: savedScrollY, behavior: 'auto' });
        if (options.focusKey) {
          const focusTarget = [...currentSection.querySelectorAll('.cart-item__qty-input')]
            .find((input) => input.dataset.key === options.focusKey);
          if (focusTarget) focusTarget.focus({ preventScroll: true });
        }
      });

      return true;
    }

    function syncLine(row, line) {
      if (!row || !line) return;
      row.dataset.key = line.key;
      const input = row.querySelector('.cart-item__qty-input');
      const linePrice = row.querySelector('[data-cart-line-price]');
      if (input) {
        input.value = line.quantity;
        input.defaultValue = line.quantity;
      }
      if (linePrice) linePrice.textContent = formatMoney(line.final_line_price);
      row.querySelectorAll('[data-key]').forEach((element) => {
        element.dataset.key = line.key;
      });
    }

    function syncCart(cart, key, row) {
      const line = cart.items.find((item) => item.key === key);
      if (line) {
        syncLine(row, line);
      } else if (row) {
        row.remove();
      }

      document.querySelectorAll('[data-cart-total]').forEach((total) => {
        total.textContent = formatMoney(cart.total_price);
      });

      const cartCount = document.querySelector('#cart-count');
      if (cartCount) cartCount.textContent = cart.item_count;

      if (cart.item_count === 0) {
        const emptyCartUrl = new URL(`${cartRoot}cart`, window.location.origin);
        const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (previewThemeId) emptyCartUrl.searchParams.set('preview_theme_id', previewThemeId);
        window.location.assign(emptyCartUrl.toString());
      }
    }

    async function updateLine(key, quantity, row) {
      if (!key) return;
      let requestStarted = false;
      showError('');
      setBusy(true);
      try {
        requestStarted = true;
        const response = await fetch(`${cartRoot}cart/change.js`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: ['main-cart'],
            sections_url: `${cartRoot}cart`
          })
        });
        if (!response.ok) throw new Error(`Cart update failed with ${response.status}`);
        const cart = await response.json();
        const replaced = replaceCartSection(cart, { focusKey: quantity > 0 ? key : '' });
        if (!replaced) window.location.assign(buildCartUrl('main-content'));
      } catch (error) {
        console.error('Unable to update cart', error);
        if (requestStarted) {
          window.location.assign(buildCartUrl('main-content'));
          return;
        }
        showError('Couldn’t update your bag. Please try again.');
        const input = row && row.querySelector('.cart-item__qty-input');
        if (input) input.value = input.defaultValue;
      } finally {
        setBusy(false);
      }
    }

    buttons.forEach((button) => {
      if (button.dataset.lnlInitialized) return;
      button.dataset.lnlInitialized = 'true';
      button.addEventListener('click', () => {
        const input = button.parentElement && button.parentElement.querySelector('.cart-item__qty-input');
        if (!input) return;
        const value = Number.parseInt(input.value, 10) || 0;
        const quantity = button.hasAttribute('data-qty-plus') ? value + 1 : Math.max(1, value - 1);
        updateLine(button.dataset.key, quantity, button.closest('[data-cart-item]'));
      });
    });

    inputs.forEach((input) => {
      if (input.dataset.lnlInitialized) return;
      input.dataset.lnlInitialized = 'true';
      input.addEventListener('change', () => {
        const quantity = Math.max(1, Number.parseInt(input.value, 10) || 1);
        updateLine(input.dataset.key, quantity, input.closest('[data-cart-item]'));
      });
    });

    removeLinks.forEach((link) => {
      if (link.dataset.lnlInitialized) return;
      link.dataset.lnlInitialized = 'true';
      link.addEventListener('click', (event) => {
        event.preventDefault();
        updateLine(link.dataset.key, 0, link.closest('[data-cart-item]'));
      });
    });

    if (duoUpsell && duoUpgrade && !duoUpgrade.dataset.lnlInitialized) {
      duoUpgrade.dataset.lnlInitialized = 'true';
      const lnlSurface = duoUpgrade.closest('[data-cart-drawer]') ? 'drawer' : 'cart_page';
      lnlTrack('lnl_duo_offer_shown', lnlSurface);
      duoUpgrade.addEventListener('click', async () => {
        lnlTrack('lnl_duo_upgrade_click', lnlSurface);
        const duoVariantId = Number.parseInt(duoUpsell.dataset.duoVariantId, 10);
        const removeKeys = [...duoUpsell.querySelectorAll('[data-duo-remove-key]')]
          .map((element) => element.dataset.duoRemoveKey)
          .filter(Boolean);
        if (!duoVariantId || removeKeys.length === 0) {
          if (duoStatus) duoStatus.textContent = 'This upgrade needs a quick refresh. Please try again.';
          return;
        }

        const originalLabel = duoUpgrade.innerHTML;
        const updates = Object.fromEntries(removeKeys.map((key) => [key, 0]));
        updates[String(duoVariantId)] = 1;
        let upgraded = false;
        let requestStarted = false;
        showError('');
        setBusy(true);
        duoUpgrade.textContent = 'Upgrading your bag…';
        if (duoStatus) duoStatus.textContent = 'Swapping your glosses for Double Trouble…';

        try {
          requestStarted = true;
          const response = await fetch(`${cartRoot}cart/update.js`, {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              updates,
              attributes: { _lnl_upsell: `duo_upgrade_${lnlSurface}` },
              sections: ['main-cart'],
              sections_url: `${cartRoot}cart`
            })
          });
          const cart = await response.json();
          if (!response.ok) throw new Error(cart.description || `Cart upgrade failed with ${response.status}`);

          const hasDuo = cart.items.some((item) => Number(item.variant_id) === duoVariantId && item.quantity >= 1);
          const keptSingle = cart.items.some((item) => removeKeys.includes(item.key));
          if (!hasDuo || keptSingle) throw new Error('The bag did not finish upgrading.');

          upgraded = true;
          lnlTrack('lnl_duo_upgrade_done', lnlSurface);
          const replaced = replaceCartSection(cart, { scrollToTop: true });
          if (!replaced) window.location.assign(buildCartUrl('main-content'));
        } catch (error) {
          console.error('Unable to upgrade cart to Double Trouble', error);
          if (requestStarted) {
            if (duoStatus) duoStatus.textContent = 'Refreshing your bag to confirm the update…';
            window.location.assign(buildCartUrl('main-content'));
            return;
          }
          duoUpgrade.innerHTML = originalLabel;
          if (duoStatus) duoStatus.textContent = 'Couldn’t upgrade your bag. Please try again.';
          showError('Couldn’t upgrade to Double Trouble. Please try again.');
        } finally {
          if (!upgraded) setBusy(false);
        }
      });
    }
  }

  function initTheme(root) {
    initHeader(root);
    initReveals(root);
    initHeroVideos(root);
    initGallery(root);
    initIngredientJump(root);
    initCart(root);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initTheme(document);
    requestAnimationFrame(() => {
      document.body.style.opacity = '1';
    });
  });

  document.body.style.opacity = '0';
  document.body.style.transition = 'opacity 0.6s ease';

  document.addEventListener('shopify:section:load', (event) => initTheme(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target;
    if (block && block.scrollIntoView) block.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
})();
